import { Fragment } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { policyMetadata } from '../../../shared/config/policyMetadata';
import { BilingualText } from '../components/BilingualText';
import { SEOHead } from '../../../shared/components/SEOHead';
import {
  legalDocuments,
  legalDocumentRoutes,
  type LegalDocumentType,
  type SectionBlockDefinition
} from '../content/legalDocuments';

interface LegalDocumentPageProps {
  documentType: LegalDocumentType;
}

export function LegalDocumentPage({ documentType }: LegalDocumentPageProps) {
  const { t } = useTranslation();
  const document = legalDocuments[documentType];

  if (!document) {
    return null;
  }

  const renderBlock = (sectionId: string, block: SectionBlockDefinition, index: number) => {
    if (block.type === 'paragraph') {
      return block.keys.map((key) => (
        <BilingualText key={`${sectionId}-paragraph-${key}`} textKey={key} as="div" className="text-base" />
      ));
    }

    if (block.type === 'bullets') {
      return (
        <ul
          key={`${sectionId}-bullets-${index}`}
          className="list-disc space-y-3 pl-5 text-base marker:text-ai-accent"
        >
          {block.keys.map((key) => (
            <li key={`${sectionId}-bullets-${key}`}>
              <BilingualText textKey={key} as="div" className="text-base" />
            </li>
          ))}
        </ul>
      );
    }

    if (block.type === 'numbers') {
      return (
        <ol
          key={`${sectionId}-numbers-${index}`}
          className="list-decimal space-y-3 pl-5 text-base marker:text-ai-accent"
        >
          {block.keys.map((key) => (
            <li key={`${sectionId}-numbers-${key}`}>
              <BilingualText textKey={key} as="div" className="text-base" />
            </li>
          ))}
        </ol>
      );
    }

    return null;
  };

  return (
    <div className="relative min-h-screen bg-ai-surface text-ai-text-primary">
      <SEOHead
        title={t(document.heroTitleKey)}
        description={t(document.heroSummaryKey)}
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-0 top-[-20vh] h-[45vh] bg-ai-radial blur-3xl" />
        <div className="absolute inset-0 bg-ai-grid bg-grid-lg opacity-30" />
      </div>
      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 pb-16 pt-10 sm:px-10 lg:px-12">
        <header className="flex flex-col gap-4 border-b border-ai-surface-glow/30 pb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-3">
            <BilingualText textKey="legal.navigation.description" as="div" className="text-sm" />
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-full border border-ai-surface-glow/40 px-5 py-2 text-sm font-semibold text-ai-text-primary transition-colors hover:border-ai-primary/50 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            >
              <BilingualText textKey="legal.navigation.backCta" as="span" className="text-sm" />
            </a>
          </div>
        </header>

        <main className="mt-8 space-y-10">
          <section className="rounded-3xl border border-ai-surface-glow/40 bg-ai-glass p-6 shadow-ai-card backdrop-blur-xl sm:p-10">
            <BilingualText textKey={document.heroTitleKey} as="h1" className="text-2xl font-semibold sm:text-3xl" />
            <BilingualText
              textKey={document.heroSummaryKey}
              as="div"
              className="mt-4 text-base text-ai-text-secondary"
            />
            <BilingualText
              textKey="legal.metadata.version"
              as="div"
              className="mt-4 text-sm text-ai-text-secondary"
              values={{
                version: policyMetadata.version,
                effectiveDate: policyMetadata.effectiveDate
              }}
            />
            {policyMetadata.changelog?.length ? (
              <div className="mt-6 space-y-4 rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/50 p-4">
                <div>
                  <BilingualText
                    textKey="legal.metadata.changelogTitle"
                    as="div"
                    className="text-base font-semibold text-ai-text-primary"
                  />
                  <BilingualText
                    textKey="legal.metadata.changelogDescription"
                    as="div"
                    className="text-sm text-ai-text-secondary"
                  />
                </div>
                <ul className="space-y-3">
                  {policyMetadata.changelog.map((entry) => (
                    <li
                      key={entry.version}
                      className="rounded-xl border border-ai-surface-glow/20 bg-ai-surface-elevated/80 p-3"
                    >
                      <BilingualText
                        textKey="legal.metadata.changelog.entryLabel"
                        as="div"
                        className="text-sm font-semibold text-ai-text-primary"
                        values={{
                          version: entry.version,
                          effectiveDate: entry.effectiveDate
                        }}
                      />
                      <BilingualText
                        textKey={entry.summaryKey}
                        as="div"
                        className="mt-2 text-sm text-ai-text-secondary"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <section className="space-y-8">
            {document.sections.map((section) => (
              <article
                key={section.id}
                className={clsx(
                  'rounded-3xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 p-6 shadow-ai-card',
                  'hover:border-ai-primary/30 transition-colors'
                )}
              >
                <BilingualText
                  textKey={section.titleKey}
                  as="h2"
                  className="text-xl font-semibold text-ai-text-primary"
                />
                <div className="mt-4 space-y-4">
                  {section.blocks.map((block, index) => (
                    <Fragment key={`${section.id}-block-${index}`}>{renderBlock(section.id, block, index)}</Fragment>
                  ))}
                </div>
              </article>
            ))}
          </section>
        </main>
      </div>
    </div>
  );
}

export function resolveLegalDocumentType(pathname: string): LegalDocumentType | null {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return legalDocumentRoutes[normalized] ?? null;
}
